// react
import React, { useEffect, useState } from 'react';

// packages
import {
  Controller,
  Control,
  FieldValues,
  UseFormWatch,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';

// components
import CountryAutocomplete from 'components/CountryAutocomplete';
import RegionAutocomplete from 'components/RegionAutocomplete';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';

// hooks
import useCountryRegion from 'hooks/useCountryRegion';

// utils
import { generateTranslation } from 'utils/translation/i18nextTranslation';
import {
  getChildSubdivisionMatchingCountryAndState,
  getCountriesList,
  getCountrySubdivisions,
  SubdivisionWithChildren,
} from 'utils/location';

// types
import type { AddressFormType } from 'utils/translation/types';
import type { AutocompleteProps } from './types';

type AddressFormProps = {
  control: Control;
  getValues: UseFormGetValues<any>;
  name: string;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<FieldValues>;
};

const AddressForm: React.VFC<AddressFormProps> = function ({
  control,
  getValues,
  name,
  setValue,
  watch,
}) {
  // constants
  const translation = generateTranslation('addressForm') as AddressFormType;
  const countryFieldName = `${name}_country`;
  const address1FieldName = `${name}_address_1`;
  const address2FieldName = `${name}_address_2`;
  const cityFieldName = `${name}_city`;
  const stateFieldName = `${name}_state`;
  const zipCodeFieldName = `${name}_zip_code`;
  const phoneFieldName = `${name}_phone`;

  // state
  const [countriesList, setCountriesList] = useState<AutocompleteProps[]>([]);
  const [statesFromCountry, setStatesFromCountry] = useState<
    SubdivisionWithChildren[]
  >([]);

  const country = watch(countryFieldName as any);
  const labels = {
    city: translation.labels.city,
    state: translation.labels.state,
    zip_code: translation.labels.zip_code,
  };
  const state = watch(stateFieldName as any);

  // get the countries list on mount
  useEffect(() => {
    const countriesList = getCountriesList().map((country) => ({
      value: country.alpha2,
      label: country.name,
    }));

    setCountriesList(countriesList);
  }, []);

  useCountryRegion({
    country,
    countryFieldName,
    getChildSubdivisionMatchingCountryAndState,
    getValues,
    setStatesFromCountry,
    setValue,
    state,
    stateFieldName,
  });

  return (
    <>
      <Controller
        name={address1FieldName as any}
        control={control}
        render={({
          field: { onChange, value, ref, ...rest },
          fieldState: { error },
        }) => (
          <TextField
            variant="filled"
            inputRef={ref}
            {...rest}
            name={address1FieldName}
            helperText={error?.message || ' '}
            error={Boolean(error)}
            onChange={onChange}
            value={value}
            label={translation.address.placeholder1}
          />
        )}
      />
      <Controller
        name={address2FieldName as any}
        control={control}
        render={({
          field: { onChange, value, ref, ...rest },
          fieldState: { error },
        }) => (
          <TextField
            variant="filled"
            inputRef={ref}
            {...rest}
            name={address2FieldName}
            helperText={error?.message || ' '}
            error={Boolean(error)}
            onChange={onChange}
            value={value}
            label={translation.address.placeholder2}
          />
        )}
      />
      <CountryAutocomplete
        initialCountry={country}
        control={control}
        name={countryFieldName}
        countriesList={countriesList}
        label={translation.menuItem}
        setValue={setValue}
      />
      <Controller
        name={cityFieldName as any}
        control={control}
        render={({
          field: { onChange, value, ref, ...rest },
          fieldState: { error },
        }) => (
          <TextField
            variant="filled"
            inputRef={ref}
            {...rest}
            name={cityFieldName}
            helperText={error?.message || ' '}
            error={Boolean(error)}
            onChange={onChange}
            value={value}
            label={labels.city}
          />
        )}
      />
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <RegionAutocomplete
            control={control}
            initialRegion={state}
            name={stateFieldName}
            country={country}
            statesFromCountry={statesFromCountry}
            getCountrySubdivisions={getCountrySubdivisions}
            getChildSubdivisionMatchingCountryAndState={
              getChildSubdivisionMatchingCountryAndState
            }
            label={labels.state}
            setValue={setValue}
          />
        </Grid>
        <Grid item sm={6} xs={12}>
          <Controller
            name={zipCodeFieldName as any}
            control={control}
            render={({
              field: { onChange, value, ref, ...rest },
              fieldState: { error },
            }) => (
              <TextField
                variant="filled"
                inputRef={ref}
                {...rest}
                name={zipCodeFieldName}
                helperText={error?.message || ' '}
                error={Boolean(error)}
                onChange={onChange}
                value={value}
                label={labels.zip_code}
              />
            )}
          />
        </Grid>
      </Grid>
      <Controller
        name={phoneFieldName as any}
        control={control}
        render={({
          field: { onChange, value, ref, ...rest },
          fieldState: { error },
        }) => (
          <TextField
            variant="filled"
            inputRef={ref}
            {...rest}
            name={phoneFieldName}
            helperText={error?.message || ' '}
            error={Boolean(error)}
            onChange={onChange}
            value={value}
            label={translation.phone}
            placeholder="+1 (555) 555-5555"
            type="tel"
          />
        )}
      />
    </>
  );
};

export default AddressForm;
